<script setup lang="ts">
import { computed, onUnmounted, reactive, ref, toRef } from 'vue';
import { EMPTY_KEYWORD_FEED_MESSAGE } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import AdpDocumentCardSkeleton from '@/components/common/adp-document/adp-document-card-skeleton.vue';
import ThemeEmptyFeed from '@/components/common/theme-empty-feed.vue';
import { useThemeSummary } from '@/components/feed-summary/composables/use-theme-summary';
import FeedSummaryGenerationButton from '@/components/feed-summary/feed-summary-generation-button.vue';
import FeedSummary from '@/components/feed-summary/feed-summary.vue';
import { useHomeSectionViewTracking } from '@/components/home/use-home-section-view-tracking';
import ThemeGeneratingCard from '@/components/theme/theme-generating-card.vue';
import { AdpDocument, Article, Theme } from '@/types';
import { syncAdpDocumentActions } from '@/utils/adpDocument';
import { useAdpDocumentOptionMenu } from '@/utils/composables/useAdpDocumentOptionMenu';
import { useTheme, useThemeKeywordFeed, useUserInfo } from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';

const props = defineProps<{
  theme: Theme;
  articleCount: number;
}>();

const layoutVariant = 'legacy';

const store = useStore();
const emitter = useEmitter();
const { data: userInfo } = useUserInfo();
const enableToEditTheme = computed(
  () =>
    userInfo.value?.role !== 'viewer' ||
    (props.theme.access_scope === 'personal' &&
      props.theme.user_id === userInfo.value?.id),
);

const { data: themeResponse } = useTheme(props.theme.id);
const themeDetail = computed(() => themeResponse.value?.themes);
const feedType = computed(() => store.state.feedType.feedType);

const optionMenu = useAdpDocumentOptionMenu({
  pageName: 'home',
  feature: 'themes_pickup',
  themeId: () => props.theme.id,
  themeExcludedSitesCount: () => props.theme.site_exclusion_ids?.length ?? 0,
  feedType: () => feedType.value,
});
const pickupMenuConfig = computed(() => ({
  undisplay: true,
  siteExclusion: !props.theme.is_chinese,
}));

const removedArticles = reactive({ items: [] as number[] });
emitter.on(
  'article-removed',
  ({
    themeId,
    articleId,
  }: {
    themeId: Theme['id'];
    articleId: Article['id'];
  }) => {
    if (themeId === props.theme.id) {
      removedArticles.items.push(articleId);
    }
  },
);

const { data: domesticFeed } =
  feedType.value === 'all'
    ? useThemeKeywordFeed(props.theme.id, ref('domestic'), undefined, ref(1), 3)
    : { data: { value: undefined } };
const { data: foreignFeed } =
  feedType.value === 'all'
    ? useThemeKeywordFeed(props.theme.id, ref('foreign'), undefined, ref(1), 3)
    : { data: { value: undefined } };

const { data } =
  feedType.value !== 'all'
    ? useThemeKeywordFeed(props.theme.id, feedType, undefined, ref(1), 5)
    : { data: { value: undefined } };

const allArticles = computed(() => {
  if (feedType.value === 'all') {
    return domesticFeed.value && foreignFeed.value
      ? [
          ...domesticFeed.value.keyword_feeds,
          ...foreignFeed.value.keyword_feeds,
        ]
      : undefined;
  } else {
    return data.value ? data.value.keyword_feeds : undefined;
  }
});

const articles = computed(() =>
  allArticles.value?.filter(a => !removedArticles.items.includes(a.id)),
);

const syncArticle = (updated: AdpDocument) =>
  syncAdpDocumentActions(articles.value ?? [], updated);
emitter.on('article-updated', syncArticle);
emitter.on('article-viewed', syncArticle);
onUnmounted(() => {
  emitter.off('article-updated', syncArticle);
  emitter.off('article-viewed', syncArticle);
});

const hasArticles = computed(() => (articles.value ?? []).length > 0);

const { summary, generateButtonState, generateSummary, shouldShowSummary } =
  useThemeSummary(
    {
      page_type: 'feed',
      feed_type: feedType.value,
      model_type: 'article',
      date: dayjs(props.theme.feed_last_updated_at).format('YYYY-MM-DD'),
      theme_id: props.theme.id,
    },
    toRef(props, 'articleCount'),
    'home',
  );

const isLoaded = computed(
  () => articles.value !== undefined && themeDetail.value !== undefined,
);

const { setRef, requestId } = useHomeSectionViewTracking(
  'themes_pickup_view',
  isLoaded,
  () => ({
    document_count: articles.value?.length ?? 0,
    theme: { id: props.theme.id },
    layout_variant: layoutVariant,
  }),
);
</script>
<template>
  <div class="o-picked-up-articles" :key="feedType" :ref="setRef">
    <div class="theme-title">
      <router-link
        class="theme-name"
        :to="{ name: 'themeFeed', params: { themeId: theme.id } }"
      >
        <DgrIcon size="default" name="hashtag" class="hashtag" />
        <div class="c-title-bold--xm">
          {{ theme.name }}
        </div>
        <div class="article-count c-text-regular--s">
          ・{{ articleCount }}件
        </div>
      </router-link>
      <FeedSummaryGenerationButton
        v-if="!theme.is_chinese"
        :state="generateButtonState"
        class="generation-button"
        @click="generateSummary()"
      />
    </div>
    <FeedSummary
      v-if="shouldShowSummary(summary)"
      :feed-summary="summary"
      class="feed-summary"
      feature="keyword_feed"
      page-name="home"
      :theme-id="theme.id"
    />
    <div class="articles" v-if="hasArticles">
      <div class="o-article" v-for="article in articles" :key="article.id">
        <hr class="divider" />
        <AdpDocumentCard
          :id="`${theme.id}-${article.id}`"
          class="adp-document-card"
          page-name="home"
          :feature="'themes_pickup'"
          :adp-document="article"
          :theme-id="theme.id"
          :show-comment-count="1"
          :feed-type="feedType"
          :disable-related-articles="false"
          :width="'100%'"
          :option-actions="
            optionMenu.buildOptionActions(article, pickupMenuConfig)
          "
          @option-action="name => optionMenu.handleOptionAction(name, article)"
          :contents-context="{
            event_name: 'themes_pickup',
            parent_request_id: requestId,
          }"
        ></AdpDocumentCard>
      </div>
    </div>
    <template v-else-if="articles && themeDetail">
      <ThemeGeneratingCard
        v-if="themeDetail.feed_last_updated_at === null"
      ></ThemeGeneratingCard>
      <ThemeEmptyFeed
        v-else
        :display-feed-type="feedType"
        :theme-feed-type="feedType === 'foreign' ? 'foreign' : 'domestic'"
        doc-type="article"
        :theme="themeDetail"
        :enable-to-edit-theme="enableToEditTheme"
        >{{ EMPTY_KEYWORD_FEED_MESSAGE }}</ThemeEmptyFeed
      >
    </template>
    <AdpDocumentCardSkeleton v-else :loading="true" class="skeleton" />
    <div class="o-footer">
      <router-link :to="{ name: 'themeFeed', params: { themeId: theme.id } }">
        <button
          class="c-btn c-btn--small c-btn--auto c-btn--outline c-text-regular--m o-load-more-button"
        >
          もっと見る
        </button>
      </router-link>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.o-picked-up-articles {
  background: $color-white;
  padding: 24px;
  border-radius: 4px;
  border: 1px solid $color-gray400;

  .adp-document-card {
    border: 0;
    padding: 0;
  }

  .theme-title {
    display: flex;
    gap: 8px;

    .hashtag {
      flex-shrink: 0;
    }
    .theme-name {
      min-width: 0;

      .c-title-bold--xm {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
    .article-count {
      flex-shrink: 0;
    }
    .generation-button {
      flex-shrink: 0;
      margin-left: auto;
    }
  }

  .theme-name {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    .article-count {
      color: $color-gray800;
    }
  }

  .feed-summary {
    margin: 8px 0;
  }

  .articles {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .skeleton {
    padding-top: 0;
    padding-left: 0;
  }

  .divider {
    border: none;
    border-top: 1px solid $color-gray200;
    padding-bottom: 16px;
    background: $color-white;
  }

  .o-footer {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    button {
      outline: none;
      background: $color-white;
      &:hover {
        background: $color-gray200;
      }
    }
  }
}
</style>
